import { gql } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { fromError } from '~utils/errors';
import { MaybePromise } from '~utils/types';
import { DeleteUser_UserFragment, useDeleteUserMutation } from './__generated__/DeleteUser.graphql';

gql`
  mutation DeleteUser($input: UserDeleteInput!) {
    userDelete(input: $input) {
      userId
    }
  }
`;

gql`
  fragment DeleteUser_user on User {
    id
    givenName
    familyName
  }
`;

interface UseDeleteUserProps {
  onCompleted?: () => MaybePromise<void>;
}

export function useDeleteUser({ onCompleted }: UseDeleteUserProps) {
  const toast = useToast();

  const [deleteUser] = useDeleteUserMutation();
  const deleteUserAction = useDestructiveAction<DeleteUser_UserFragment>({
    title: 'Delete User',
    message: (user) => (
      <>
        {t('deleteUserConfirm')} <strong>{`${user?.givenName} ${user.familyName}`}</strong>?
      </>
    ),
    onConfirm: async (user) => {
      try {
        await deleteUser({
          variables: {
            input: {
              userId: user.id,
            },
          },
        });
        await onCompleted?.();
      } catch (err) {
        toast({
          status: 'error',
          title: t('deleteUserErr'),
          description: fromError(err, 'DeleteUser', {
            ORGANIZATION_CANNOT_DELETE_OWNER: t('deleteUserErrDesc'),
            UNAUTHORIZED: t('deleteUserErrDesc1'),
          }),
        });
      }
    },
  });

  return {
    mutation: deleteUser,
    action: deleteUserAction,
  };
}
